import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [mfaStep, setMfaStep] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const recaptchaContainerRef = useRef(null);
    const navigate = useNavigate();
    
    const { user, login, loginWithGoogle, sendMfaSms, verifyMfaCode, authError, setAuthError } = useContext(AuthContext);

    // If there's an authError from the context (e.g. they logged in with Google but don't exist in Postgres), show it.
    useEffect(() => {
        if (authError) {
            setError(authError);
            setAuthError(null); // Clear it after displaying
        }
    }, [authError, setAuthError]);

    // Automatically redirect when the user is successfully fully authenticated in Postgres
    useEffect(() => {
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await login(email, password);
            if (result && result.requiresMFA) {
                // If the user requires MFA, move to the MFA step
                setMfaStep(true);
                // Extract the masked phone number hint
                const hint = result.resolver.hints[0].phoneNumber;
                setPhoneNumber(hint);
            }
            // If MFA is not required, we wait for AuthContext onAuthStateChanged to populate `user`, 
            // which will trigger the useEffect above to navigate.
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/invalid-credential') {
                setError('Invalid email or password. Please try again.');
            } else {
                setError(err.message || 'An error occurred during login.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        setLoading(true);
        try {
            await loginWithGoogle();
            // We don't navigate here because onAuthStateChanged handles it, and if it fails, authError is set.
        } catch (err) {
            console.error(err);
            setError(err.message || 'An error occurred during Google sign in.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendCode = async () => {
        setError('');
        setLoading(true);
        try {
            await sendMfaSms(phoneNumber, recaptchaContainerRef.current);
            setCodeSent(true);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to send verification code.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await verifyMfaCode(mfaCode);
            // Will navigate automatically when user populates
        } catch (err) {
            console.error(err);
            setError(err.message || 'Invalid verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <div className="glass-card" style={{ padding: '40px', width: '320px' }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <img src="/logo-brand.png" alt="Wagoing Logo" style={{ height: '60px', marginBottom: '10px' }} />
                    <h2 style={{ margin: 0 }}>Parking Portal Login</h2>
                </div>
                
                {error && <div style={{ color: 'var(--danger)', marginBottom: '15px', fontSize: '14px', textAlign: 'center' }}>{error}</div>}
                
                {!mfaStep ? (
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Email</label>
                            <input 
                                type="email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                required 
                            />
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Password</label>
                            <input 
                                type="password" 
                                value={password} 
                                onChange={(e) => setPassword(e.target.value)} 
                                required 
                            />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', fontSize: '16px', padding: '12px', marginBottom: '15px' }}>
                            {loading ? 'Logging in...' : 'Log In'}
                        </button>
                        
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border)' }}></div>
                            <span style={{ padding: '0 10px', color: 'var(--text-muted)', fontSize: '14px' }}>OR</span>
                            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border)' }}></div>
                        </div>

                        <button 
                            type="button" 
                            onClick={handleGoogleLogin} 
                            disabled={loading} 
                            style={{ 
                                width: '100%', 
                                fontSize: '16px', 
                                padding: '12px', 
                                backgroundColor: 'white', 
                                color: '#757575', 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: '18px', height: '18px' }} />
                            Sign in with Google
                        </button>
                    </form>
                ) : (
                    <div>
                        <h4 style={{ textAlign: 'center', marginBottom: '15px' }}>Two-Factor Authentication</h4>
                        <p style={{ fontSize: '14px', textAlign: 'center', marginBottom: '20px' }}>
                            Your account requires extra verification. A code will be sent to {phoneNumber}.
                        </p>
                        
                        <div ref={recaptchaContainerRef}></div>

                        {!codeSent ? (
                            <button onClick={handleSendCode} className="btn btn-primary" disabled={loading} style={{ width: '100%', fontSize: '16px', padding: '12px' }}>
                                {loading ? 'Sending...' : 'Send SMS Code'}
                            </button>
                        ) : (
                            <form onSubmit={handleVerifyCode}>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Enter Code</label>
                                    <input 
                                        type="text" 
                                        value={mfaCode} 
                                        onChange={(e) => setMfaCode(e.target.value)} 
                                        placeholder="123456"
                                        required 
                                    />
                                </div>
                                <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', fontSize: '16px', padding: '12px' }}>
                                    {loading ? 'Verifying...' : 'Verify & Log In'}
                                </button>
                            </form>
                        )}
                    </div>
                )}
                
                <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '14px' }}>
                    Don't have an account? <span style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => navigate('/signup')}>Sign Up</span>
                </div>
            </div>
        </div>
    );
};

export default Login;